import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { PlusCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Layout, Menu, Dropdown, Avatar, Button, Badge, message } from 'antd';
import { signOutWithPush } from '../../lib/webPush';
import { useAppSelector } from '../../store/hooks';
import {
  selectHasOffer,
  selectHasPartnerInvite,
  selectUser,
  selectUserData,
} from '../../store/slices/userSlice';
import { GUEST_MENU, MEMBER_MENU } from '../../shared/variables/menus';
import Navigation from './Navigation';
import TopMenu from './TopMenu';
import {
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_MY_PROFILE,
  ROUTE_PLAY,
} from '../../shared/variables/routes';
import Link from 'next/link';
import SearchBox from './SearchBox';
import MobileMenuDrawer from './MobileMenuDrawer';
import { useInstallApp } from '../../lib/installApp';
import { useProfileLanguageSync } from '../../lib/profileLanguage';
import { storageImage } from '../../shared/utils/storage-image';
import { useCongratsBadge } from '../../lib/congratsBadge';

export default function Header() {
  const router = useRouter();
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const userData = useAppSelector(selectUserData);
  const hasOffer = useAppSelector(selectHasOffer);
  const hasPartnerInvite = useAppSelector(selectHasPartnerInvite);
  const hasIndicator = hasOffer || hasPartnerInvite;
  // Ç7: son akış ziyaretinden bu yana yeni tebrik sayısı (yoksa teklif/davet noktası).
  const newCongrats = useCongratsBadge(userData?.id);
  const badgeProps = newCongrats > 0 ? { count: newCongrats, overflowCount: 9, size: 'small' as const, className: 'header-congrats-badge' } : { dot: hasIndicator };
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const installApp = useInstallApp();
  // Bildirim dili: seçili site dili profile yazılır (sunucu push/e-posta bu dilde gider).
  useProfileLanguageSync(userData);

  const showingMenu: any[] = user ? MEMBER_MENU : GUEST_MENU;

  const menuItemClickHandler = async (e: any, action: string) => {
    if (action === 'signOut') {
      e.preventDefault();
      try { await signOutWithPush(); }
      catch { message.error(t('errors.pushLogoutFailed')); return; }
      await router.push(ROUTE_LOGIN);
    }
  };

  const menu = (
    <Menu>
      {showingMenu
        .filter(item => item.active)
        .map((item, index) => (
          <Menu.Item key={`menu-${index}`}>
            <Badge size="default" offset={[10, 0]} dot={item.showDot && hasOffer}>
              <Link href={item.url}>
                <a
                  href={item.url}
                  className="dark-grey-text"
                  onClick={e => menuItemClickHandler(e, item.clickAction)}
                >
                  {t(item.text)}
                </a>
              </Link>
            </Badge>
          </Menu.Item>
        ))}
      {installApp.path === 'native' && (
        <Menu.Item key="install-app">
          <button type="button" className="header-install-app" onClick={installApp.install}>
            {t('installApp.menuItem')}
          </button>
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Layout.Header className="header">
      <TopMenu />

      <div className="header-content">
        <Link href={ROUTE_HOME}>
          <a className="header-content-logo">
            <img src="/images/logo.svg" alt="tenis.io" width={150} height={50} />
          </a>
        </Link>

        <div className="header-content-search">
          <SearchBox />
        </div>

        <button
          type="button"
          ref={menuTrigger}
          className="header-mobile-toggle"
          aria-label={
            newCongrats > 0
              ? `${t('header.openAccountMenu')} · ${t('header.newCongrats', { count: newCongrats })}`
              : t('header.openAccountMenu')
          }
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Badge {...badgeProps}>
            <Avatar
              size={32}
              src={storageImage(userData?.foto_mini_url || userData?.foto_url, 32)}
              icon={userData?.ad ? undefined : <UserOutlined aria-hidden="true" />}
            >
              {userData?.ad?.[0]?.toLocaleUpperCase('tr-TR')}
            </Avatar>
          </Badge>
        </button>

        <div className="header-content-dropdowns">
          <Dropdown overlay={menu}>
            {/* Gerçek href SEO "crawlable anchors" denetimi için: hover menüyü açar, tıklama profile/girişe gider */}
            <a
              className="ant-dropdown-link"
              href={user?.uid ? ROUTE_MY_PROFILE : ROUTE_LOGIN}
              onClick={e => {
                e.preventDefault();
                router.push(user?.uid ? ROUTE_MY_PROFILE : ROUTE_LOGIN);
              }}
            >
              <Badge size="default" {...badgeProps}>
                <div
                  className={`header-content-dropdowns-user-box ${
                    user?.uid ? 'header-content-dropdowns-user-box-active' : ''
                  }`}
                >
                  <Avatar
                    size={32}
                    src={storageImage(userData?.foto_mini_url, 32)}
                    icon={<UserOutlined />}
                  />
                  <span className="header-content-dropdowns-user-box-text">
                    {user?.uid ? (
                      <>{userData ? `${userData?.ad} ${userData?.soyad}` : t('header.account')}</>
                    ) : (
                      <>
                        {t('header.loginCta')}
                        <br />
                        <small>{t('header.loginCtaSub')}</small>
                      </>
                    )}
                  </span>
                </div>
              </Badge>
            </a>
          </Dropdown>

          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={() => router.push(user?.uid ? ROUTE_PLAY : `${ROUTE_LOGIN}?redirect=${encodeURIComponent(ROUTE_PLAY)}`)}
            title={!user?.uid ? t('header.loginToPlay', 'Maç teklif etmek için giriş yap') : undefined}
          >
            {t('cta.proposeMatch')}
          </Button>
        </div>
      </div>

      <Navigation />

      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onClosed={() => menuTrigger.current?.focus()}
      />
    </Layout.Header>
  );
}
